const LEVEL_RANK = {
  R: 1,
  W: 2,
  X: 3,
};

export const CAPABILITIES = {
  dashboard: "app.dashboard",
  dashboardCardActiveCustomers: "dashboard.card.activeCustomers",
  dashboardCardFeeRevenue: "dashboard.card.feeRevenue",
  dashboardCardProspects: "dashboard.card.prospects",
  dashboardCardFeeMonitor: "dashboard.card.feeMonitor",
  dashboardCardActiveCustomersChart: "dashboard.card.activeCustomersChart",
  profile: "app.profile",
  notifications: "app.notifications",
  customersRead: "customers.read",
  customersCreate: "customers.create",
  customersEdit: "customers.edit",
  customersCardWrite: "customers.card.write",
  customersDocuments: "customers.documents",
  bankAccountRead: "bankaccount.read",
  bankAccountWrite: "bankaccount.write",
  customersPricing: "customers.pricing",
  customersVfFlags: "customers.vfFlags",
  customersProvisionUsers: "customers.provisionUsers",
  invoicesWrite: "invoices.write",
  claimsWrite: "claims.write",
  claimsSubmitXml: "claims.submitXml",
  claimsUndo: "claims.undo",
  prefinanceRead: "prefinance.read",
  prefinanceCreate: "prefinance.create",
  prefinanceFinalize: "prefinance.finalize",
  prefinanceUnfinalize: "prefinance.unfinalize",
  feeMonitorRead: "feemonitor.read",
  prospectsRead: "prospects.read",
  prospectsWrite: "prospects.write",
  prospectsConvert: "prospects.convert",
  refundRatesRead: "refundrates.read",
  refundRatesWrite: "refundrates.write",
  settingsGlobal: "settings.global",
  settingsReference: "settings.reference",
  settingsReferenceDelete: "settings.reference.delete",
  adminPanel: "admin.panel",
  adminAssignAd2: "admin.assignAd2",
  adminUsers: "admin.users",
  adminMigrateLegacy: "admin.migrateLegacy",
};

export const ROUTE_PERMISSIONS = [
  {
    prefix: "/settings/adminsettings/accountmanagers",
    anyOf: [
      { capability: CAPABILITIES.settingsGlobal, minLevel: "R" },
      { capability: CAPABILITIES.customersProvisionUsers, minLevel: "X" },
    ],
  },
  { prefix: "/dashboard", capability: CAPABILITIES.dashboard },
  { prefix: "/profile", capability: CAPABILITIES.profile },
  { prefix: "/notifications", capability: CAPABILITIES.notifications },
  { prefix: "/customers", capability: CAPABILITIES.customersRead },
  { prefix: "/sales/prospectmanagement", capability: CAPABILITIES.prospectsRead },
  { prefix: "/sales/feemonitor", capability: CAPABILITIES.feeMonitorRead },
  { prefix: "/invoices", capability: CAPABILITIES.invoicesWrite },
  { prefix: "/claims", capability: CAPABILITIES.claimsWrite },
  { prefix: "/financial", capability: CAPABILITIES.prefinanceRead },
  { prefix: "/settings/adminsettings", capability: CAPABILITIES.settingsGlobal },
  { prefix: "/settings/portalsettings", capability: CAPABILITIES.settingsGlobal },
  { prefix: "/settings/companysettings", capability: CAPABILITIES.settingsGlobal },
  { prefix: "/settings/globalsettings/refundrates", capability: CAPABILITIES.refundRatesRead },
  { prefix: "/settings/globalsettings/suppliers", capability: CAPABILITIES.settingsGlobal },
  { prefix: "/settings/globalsettings/supplierportalinfo", capability: CAPABILITIES.settingsGlobal },
  { prefix: "/settings/globalsettings/taxoffices", capability: CAPABILITIES.settingsGlobal },
  { prefix: "/settings/globalsettings", capability: CAPABILITIES.adminPanel },
  { prefix: "/admin", capability: CAPABILITIES.adminPanel },
];

export const meetsLevel = (actual, minimum = "R") => {
  const actualRank = LEVEL_RANK[actual];
  const minimumRank = LEVEL_RANK[minimum] ?? LEVEL_RANK.R;
  return Boolean(actualRank) && actualRank >= minimumRank;
};

export const hasPermission = (permissions, capabilityId, minimum = "R") => {
  if (!capabilityId) {
    return true;
  }

  if (!Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  const grant = permissions.find((permission) => permission?.id === capabilityId);
  return meetsLevel(grant?.level, minimum);
};

export const findRoutePermission = (pathname) => {
  const normalized = (pathname || "").toLowerCase();
  return [...ROUTE_PERMISSIONS]
    .sort((left, right) => right.prefix.length - left.prefix.length)
    .find((route) => normalized === route.prefix.toLowerCase() || normalized.startsWith(`${route.prefix.toLowerCase()}/`));
};

export const allowsRoute = (route, permissions) => {
  if (!route) {
    return true;
  }

  if (Array.isArray(route.anyOf) && route.anyOf.length > 0) {
    return route.anyOf.some((option) => hasPermission(permissions, option.capability, option.minLevel || "R"));
  }

  return hasPermission(permissions, route.capability, route.minLevel || "R");
};

const pageIsAllowed = (page, permissions) => {
  if (Array.isArray(page.anyOf) && page.anyOf.length > 0) {
    return page.anyOf.some((option) => hasPermission(permissions, option.capability, option.minLevel || "R"));
  }

  if (page.capability) {
    return hasPermission(permissions, page.capability, page.minLevel || "R");
  }

  return true;
};

const filterPages = (pages, permissions) => {
  if (!Array.isArray(pages)) {
    return [];
  }

  return pages
    .map((page) => {
      if (page.children) {
        const children = filterPages(page.children, permissions);
        if (children.length === 0) {
          return null;
        }

        return { ...page, children };
      }

      if (!pageIsAllowed(page, permissions)) {
        return null;
      }

      return page;
    })
    .filter(Boolean);
};

export const filterNavItems = (sections, permissions) => {
  if (!Array.isArray(sections)) {
    return [];
  }

  return sections
    .map((section) => ({
      ...section,
      pages: filterPages(section.pages, permissions),
    }))
    .filter((section) => section.pages.length > 0);
};
